import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import {
  IPersonalizedNs,
  PILLAR_NS_MAP,
  PillarPage
} from 'src/constants/searchPages'
import { IPost } from 'src/types/hygraph'
import { getPostTopic } from 'src/utils/hygraph'

type IProps = {
  firstBlogPost: IPost | null
  pillar?: PillarPage
  /**
   * Read the four `BLOG.*` keys from this namespace instead of the pillar's.
   *
   * The pillar namespaces are the biggest in the repo (44 kB for
   * `page_estacionamiento_y_pension`), and `serverSideTranslations` ships a
   * listed namespace WHOLE into `__NEXT_DATA__` for every visitor. On a page
   * that renders this section but no other pillar-namespace consumer, that is
   * 44 kB of payload for four strings — measured 2026-09-08 as 37 kB of the
   * 134 kB `_nextI18Next` blob on `/pension-estacionamiento/flotillas`, a
   * top paid lander whose Android visitors already sit at a 4.4 s LCP.
   *
   * `useLazyNamespace` is the wrong tool here: this copy is server-rendered
   * and above the FAQ, so it must not key-flash. Pointing at a namespace the
   * page already loads costs nothing and keeps the strings in the SSR HTML.
   */
  ns?: IPersonalizedNs | 'anfitrion' | 'flotillas'
}

const BlogSection = ({ firstBlogPost, pillar, ns }: IProps) => {
  const { t } = useTranslation(
    ns ?? (pillar ? PILLAR_NS_MAP[pillar] : 'anfitrion')
  )

  if (!firstBlogPost || !firstBlogPost.slug) return null

  const { basePath } = getPostTopic(firstBlogPost)
  if (!basePath) return null

  return (
    <section className="border-t border-ink-100/15 bg-cream-200 py-10">
      <div className="mx-auto max-w-7xl px-4">
        <div className="overflow-hidden rounded-xl bg-white-500 ring-1 ring-ink-100/15">
          <div className="flex flex-col md:flex-row">
            {/* Image Section */}
            <div className="relative h-48 w-full md:h-auto md:w-2/5">
              <Image
                src={firstBlogPost.coverImage?.url ?? ''}
                alt={firstBlogPost.coverImageAlt ?? ''}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 40vw"
                quality={75}
                priority
              />
            </div>

            {/* Content Section */}
            <div className="flex flex-col justify-between p-4 md:w-3/5 md:p-6">
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <span className="inline-block rounded-full bg-blue-300/20 px-3 py-1 text-xs font-medium text-blue-500">
                    {t('BLOG.LATEST_ARTICLE')}
                  </span>
                  <span className="text-xs font-medium text-ink-200">
                    {t('BLOG.TOPIC_TITLE')}
                  </span>
                </div>
                <h3 className="mb-2 line-clamp-2 text-lg font-semibold text-ink-700 md:text-xl">
                  {firstBlogPost.title}
                </h3>
                <p className="mb-4 line-clamp-2 text-sm text-ink-200 md:text-base">
                  {firstBlogPost.description}
                </p>
              </div>

              <div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:gap-6">
                <Link
                  href={`${basePath}/${firstBlogPost.slug}`}
                  className="group flex items-center text-sm font-medium text-blue-500 transition-colors duration-300 hover:text-blue-600 md:text-base"
                >
                  {t('BLOG.READ_ARTICLE')}
                  <span className="ml-1 transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </Link>
                <Link
                  href={basePath}
                  className="group flex items-center text-sm font-medium text-ink-200 transition-colors duration-300 hover:text-ink-700 md:text-base"
                >
                  {t('BLOG.VIEW_ALL_ARTICLES')}
                  <span className="ml-1 transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default React.memo(BlogSection)
